<template>
	<v-card
		class='directory-page pa-7 mt-8'
		style='border-radius: 10px; max-width: 600px;'
		flat
	>
		<v-card-text class='mt-5'>
			<div class='d-flex justify-space-between'>
				<div class='page-title'>
					<router-link
						active-class='active'
						:to="{
							name: 'directory',
							query: {
								category_id: this.categoryId
							}
						}"
						exact
						class='text-decoration-none d-flex'
					>
						<img
							:src='arrowIcon'
							class='mr-3'
							alt='arrow-icon'
						>
						<span class='text-decoration-underline font-weight-bold'>Back</span>
					</router-link>
				</div>
			</div>

			<div
				v-if='!isDirectoryEmpty'
			>

				<div class='user__brief-info-with-avatar d-flex my-10'>
					<Avatar
						:avatar='avatarPath'
						size='140px'
						border-radius='square'
						class='user__avatar mr-10'
					/>

					<div class='user__brief-description'>
						<h1 class='mb-3'>{{ userName }}</h1>

						<div class='d-flex flex-column'>

							<span
								v-if='companyName'
								class='user__brief-description__company mb-1'
							>{{ companyName }}</span>

							<span
								v-if='heldPosition'
								class='user__brief-description__held-position mb-1'
							>{{ heldPosition }}</span>

							<span
								v-if='location'
								class='user__brief-description__location mb-1'
							>{{ location }}</span>

						</div>
					</div>
				</div>

				<!-- About me -->
				<div
					v-if='isAboutMeShow'
					class='user__about-me my-8'
				>
					<h3 class='mb-3'>{{ aboutMeTitle }}</h3>

					<p v-if='userDepartmentTitle && userDepartmentValue'>
						<span class='font-weight-bold mr-2'>{{ userDepartmentTitle }}: </span>
						<span>{{ userDepartmentValue }}</span>
					</p>

					<p v-if='employmentTypeTitle && employmentTypeValue'>
						<span class='font-weight-bold mr-2'>{{ employmentTypeTitle }}: </span>
						<span>{{ employmentTypeValue }}</span>
					</p>

					<p v-if='userEmailTitle && userEmailValue'>
						<span class='font-weight-bold mr-2'>{{ userEmailTitle }}: </span>
						<a :href="'mailto:' + userEmailValue">{{ userEmailValue }}</a>
					</p>
				</div>

				<!-- Profile Info -->
				<div 
					v-if="isProfileInfoShow"
					class="user__profile-info my-8"
				>
					<h3 class='mb-3'>{{ profileInfoSectionTitle }}</h3>
					<p v-if='userProfileEmailTitle && userProfileEmailValue'>
						<span class='font-weight-bold mr-2'>{{ userProfileEmailTitle }}: </span>
						<a :href="'mailto:' + userProfileEmailValue">{{ userProfileEmailValue }}</a>
					</p>
					<p v-if='userCompanyTitle && userCompanyValue'>
						<span class='font-weight-bold mr-2'>{{ userCompanyTitle }}: </span>
						<span>{{ userCompanyValue }}</span>
					</p>
				</div>

				<!-- Areas of interest -->
				<div
					v-if="isAreasShow"
					class="user_areas my-8"
				>
					<h3 class='mb-3'>{{ areasSectionTitle }}</h3>
					<p v-if='areasTitle && areasValue'>
						<span class='font-weight-bold mr-2'>{{ areasTitle }}: </span>
						<span>{{ areasValue }}</span>
					</p>
				</div>

				<!-- Chapter -->
				<div
					v-if="isChapterShow"
					class="user_chapter my-8"
				>
					<h3 class='mb-3'>{{ chapterSectionTitle }}</h3>
					<p v-if='chapterTitle && chapterValue'>
						<span class='font-weight-bold mr-2'>{{ chapterTitle }}: </span>
						<span>{{ chapterValue }}</span>
					</p>
				</div>

				<!-- Bio -->
				<div
					v-if='userDescriptionValue'
					class='user__bio my-8'
				>
					<h3 class='mb-3'>{{ bioTitle }}</h3>
					<p v-html='userDescriptionValue'></p>
				</div>

				<!-- Networks -->
				<div
					v-if='isNetworksShow'
					class='user__networks my-8'
				>
					<h3 class='mb-3'>{{ networksTitle }}</h3>

					<v-list>
						<v-list-item v-if='customUrlValue'>
							<v-icon>mdi-link-variant</v-icon>
							<a 
								class="user__networks-link"
								:href='customUrlValue' 
								target='_blank'
							>
								{{ customUrlValue }}
							</a>
						</v-list-item>

						<v-list-item v-if='facebookValue'>
							<v-icon>mdi-facebook</v-icon>
							<a 
								class="user__networks-link"
								:href='facebookValue'
								target="_blank"
							>
								{{ facebookValue }}
							</a>
						</v-list-item>

						<v-list-item v-if='linkedinValue'>
							<v-icon>mdi-linkedin</v-icon>
							<a 
								class="user__networks-link"
								:href='linkedinValue'
								target="_blank"
							>
								{{ linkedinValue }}
							</a>
						</v-list-item>

						<v-list-item v-if='twitterValue'>
							<v-icon>mdi-twitter</v-icon>
							<a 
								class="user__networks-link"
								:href='twitterValue'
								target="_blank"
							>
								{{ twitterValue }}
							</a>
						</v-list-item>
					</v-list>
				</div>

				<!-- Phone numbers -->
				<div
					v-if='isPhoneNumbersShow'
					class='user__phone-numbers my-8'
				>
					<h3 class='mb-3'>{{ phoneNumbersTitle }}</h3>

					<v-list>
						<v-list-item v-if='userCellPhoneValue'>
							<v-icon>mdi-cellphone</v-icon>
							<a @click="copyPhone(userCellPhoneValue)" style="text-decoration: underline">
								{{ userCellPhoneValue }}
							</a>
						</v-list-item>

						<v-list-item v-if='userWorkPhoneValue'>
							<v-icon>mdi-phone</v-icon>
							<a @click="copyPhone(userWorkPhoneValue)" style="text-decoration: underline">
								{{ userWorkPhoneValue }}
							</a>
						</v-list-item>
					</v-list>
				</div>

				<!-- Address -->
				<div
					v-if='isWorkAddressShow'
					class='user__work-address pa-5 my-8'
				>
					<h3 class='mb-3'>{{ workAddressTitle }}:</h3>

					<v-divider class='mb-5 mt-5'></v-divider>

					<p v-if='firstAddressCityValue && firstAddressCityTitle'>
						<span class='font-weight-bold mr-2'>{{ firstAddressCityTitle }}: </span>
						<span>{{ firstAddressCityValue }}</span>
					</p>

					<p v-if='firstAddressStateValue && firstAddressStateTitle'>
						<span class='font-weight-bold mr-2'>{{ firstAddressStateTitle }}: </span>
						<span>{{ firstAddressStateValue }}</span>
					</p>

					<p v-if='firstAddressCountryValue && firstAddressCountryTitle'>
						<span class='font-weight-bold mr-2'>{{ firstAddressCountryTitle }}: </span>
						<span>{{ firstAddressCountryValue }}</span>
					</p>
				</div>
			</div>

			<v-skeleton-loader
				style='width: 100%;'
				v-bind='attrs'
				type='card'
				v-if='isDirectoryEmpty'
			>
			</v-skeleton-loader>
		</v-card-text>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import searchIcon from '@/assets/images/search.svg'
import arrowIcon from '@/assets/images/resourcesIcon/arrow-icon.svg'
import Avatar from '@/components/Avatar'
import _ from 'lodash'

export default {
	name: 'DirectoryPage',
	components: {
		Avatar,
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		...mapGetters('directory', ['pageDirectory', 'directory']),
		...mapGetters('event', ['attendee']),
		...mapGetters('app', ['isShowAlert', 'alertMessage']),

		isDirectoryEmpty() {
			const {
				directory_id: directoryId,
			} = this.directory || {}

			return directoryId !== +this.$route.params.id
		},

		categoryId() {
			if (+this.$route.query.category_id) {
				return +this.$route.query.category_id;
			}
			return null
		},

		allowFields() {
			const {
				public: profileFields = [],
			} = this.directory || {}

			return profileFields.reduce((prev, current) => {
				return { ...prev, [current.field]: current }
			}, {})


		},
		isVerticalTabs() {
			return this.$vuetify.breakpoint.mdAndUp
		},

		// short profile
		location() {
			const { directory_forth_row: location } = this.directory || {}
			return location
		},
		userName() {
			const {
				directory_first_row: firstName,
			} = this.directory || {}

			return `${firstName}`
		},
		avatarPath() {
			const { directory_logo: logo } = this.directory || {}
			return logo
		},
		heldPosition() {
			const { directory_third_row: heldPosition } = this.directory || {}
			return heldPosition
		},
		companyName() {
			const { directory_second_row: company } = this.directory || {}
			return company
		},

		// About me
		aboutMeTitle() {
			const {
				member_employment_type: employmentType = {},
				member_email: email = {},
				dir_index_column3: department = {}
			} = this.allowFields

			if (!_.isEmpty(email)) {
				return email.section
			}

			if (!_.isEmpty(employmentType)) {
				return employmentType.section
			}

			if (!_.isEmpty(department)) {
				return department.section
			}

			return 'About me'
		},
		isAboutMeShow() {
			return this.userEmailTitle && this.userEmailValue ||
				this.employmentTypeTitle && this.employmentTypeValue ||
				this.userDepartmentTitle && this.userDepartmentValue
		},
		isProfileInfoShow() {
			return this.userProfileEmailTitle && this.userProfileEmailValue ||
			this.userCompanyValue && this.userCompanyTitle
		},

		employmentTypeTitle() {
			const { member_employment_type: employmentType = {} } = this.allowFields
			if (!_.isEmpty(employmentType)) {
				return employmentType.title
			}
			return null
		},
		employmentTypeValue() {
			const { member_employment_type: employmentType = {} } = this.allowFields
			if (!_.isEmpty(employmentType)) {
				return employmentType.value
			}
			return null
		},
		userEmailTitle() {
			const {
				member_email: userEmail = {}
			} = this.allowFields
			if (!_.isEmpty(userEmail)) {
				return userEmail.title
			}
			return null
		},
		userEmailValue() {
			const {
				member_email: userEmail = {}
			} = this.allowFields
			if (!_.isEmpty(userEmail)) {
				return userEmail.value
			}
			return null
		},
		userDepartmentTitle() {
			const {
				dir_index_column3: department = {},
			} = this.allowFields
			if (!_.isEmpty(department)) {
				return department.title
			}
			return null
		},
		userDepartmentValue() {
			const {
				dir_index_column3: department = {},
			} = this.allowFields
			if (!_.isEmpty(department)) {
				return department.value
			}
			return null
		},
		profileInfoSectionTitle() {
			const {
				email,
				company: companyInfo
			} = this.allowFields
			if (!_.isEmpty(email)) {
				return email.section
			}
			if (!_.isEmpty(companyInfo)) {
				return companyInfo.section
			}
			return null
		},
		userProfileEmailTitle() {
			const {
				email: userEmail = {}
			} = this.allowFields
			if (!_.isEmpty(userEmail)) {
				return userEmail.title
			}
			return null
		},
		userProfileEmailValue() {
			const {
				email: userEmail = {}
			} = this.allowFields
			if (!_.isEmpty(userEmail)) {
				return userEmail.value
			}
			return null
		},
		userCompanyTitle() {
			const {
				company: companyInfo
			} = this.allowFields
			if (!_.isEmpty(companyInfo)) {
				return companyInfo.title
			}
			return null
		},
		userCompanyValue() {
			const {
				company: companyInfo
			} = this.allowFields
			if (!_.isEmpty(companyInfo)) {
				return companyInfo.value
			}
			return null
		},


		// areas
		isAreasShow() {
			return this.areasTitle && this.areasValue
		},
		areasSectionTitle() {
			const {
				areas_of_interest: areas
			} = this.allowFields
			if (!_.isEmpty(areas)) {
				return areas.section
			}
			return null
		},
		areasTitle() {
			const {
				areas_of_interest: areas
			} = this.allowFields
			if (!_.isEmpty(areas)) {
				return areas.title
			}
			return null
		},
		areasValue() {
			const {
				areas_of_interest: areas
			} = this.allowFields
			if (!_.isEmpty(areas)) {
				return areas.value
			}
			return null
		},

		// chapter
		isChapterShow() {
			return this.chapterTitle && this.chapterValue
		},
		chapterTitle() {
			const {
				chapter
			} = this.allowFields
			if (!_.isEmpty(chapter)) {
				return chapter.title
			}
			return null
		},
		chapterValue() {
			const {
				chapter
			} = this.allowFields
			if (!_.isEmpty(chapter)) {
				return chapter.value
			}
			return null
		},
		chapterSectionTitle() {
			const {
				chapter
			} = this.allowFields
			if (!_.isEmpty(chapter)) {
				return chapter.section
			}
			return null
		},

		// bio
		bioTitle() {
			const {
				member_bio: userDescription = {},
			} = this.allowFields

			if (!_.isEmpty(userDescription)) {
				return userDescription.section
			}

			return 'Bio'
		},
		userDescriptionValue() {
			const { member_bio: userDescription = {} } = this.allowFields
			if (!_.isEmpty(userDescription)) {
				return userDescription.value
			}
			return null
		},

		// networks
		networksTitle() {
			const {
				member_site: site = {},
				member_linkedin: linkedin = {},
				member_facebook: facebook = {},
				member_twitter: twitter = {},
			} = this.allowFields

			if (!_.isEmpty(site)) {
				return site.section
			}

			if (!_.isEmpty(linkedin)) {
				return linkedin.section
			}

			if (!_.isEmpty(facebook)) {
				return facebook.section
			}

			if (!_.isEmpty(twitter)) {
				return twitter.section
			}

			return 'Networks'
		},
		isNetworksShow() {
			return this.customUrlValue ||
				this.twitterValue ||
				this.linkedinValue ||
				this.facebookValue
		},
		customUrlValue() {
			const { member_site: social = {} } = this.allowFields

			return this.formatUrl(social.value)
		},
		twitterValue() {
			const { member_twitter: social = {}, twitter: social1 = {} } = this.allowFields
			if (!_.isEmpty(social)) {
				return this.formatUrl(social.value)
			}
			if (!_.isEmpty(social1)) {
				return this.formatUrl(social1.value)
			}
			return null
		},
		linkedinValue() {
			const { member_linkedin: social = {}, linkedin: social1 = {} } = this.allowFields
			if (!_.isEmpty(social)) {
				return this.formatUrl(social.value)
			}
			if (!_.isEmpty(social1)) {
				return this.formatUrl(social1.value)
			}
			return null
		},
		facebookValue() {
			const { member_facebook: social = {}, facebook: social1 = {} } = this.allowFields
			if (!_.isEmpty(social)) {
				return this.formatUrl(social.value)
			}
			if (!_.isEmpty(social1)) {
				return this.formatUrl(social1.value)
			}
			return null
		},

		// phone numbers
		phoneNumbersTitle() {
			const {
				member_phone: phone = {},
				member_work_phone: workPhone = {},
			} = this.allowFields

			if (!_.isEmpty(phone)) {
				return phone.section
			}

			if (!_.isEmpty(workPhone)) {
				return workPhone.section
			}

			return 'Phone numbers'
		},
		isPhoneNumbersShow() {
			return this.userWorkPhoneValue || this.userCellPhoneValue
		},
		userCellPhoneValue() {
			const { member_phone: phone = {} } = this.allowFields
			if (!_.isEmpty(phone)) {
				return phone.value
			}
			return null
		},
		userWorkPhoneValue() {
			const { member_work_phone: phone = {} } = this.allowFields
			if (!_.isEmpty(phone)) {
				return phone.value
			}
			return null
		},

		// addresses
		workAddressTitle() {
			const {
				dir_index_column1: country = {},
				dir_index_column2: state = {},
				member_city: city = {},
			} = this.allowFields

			if (!_.isEmpty(country)) {
				return country.section
			}

			if (!_.isEmpty(state)) {
				return state.section
			}

			if (!_.isEmpty(city)) {
				return city.section
			}

			return 'Address info'

		},
		isWorkAddressShow() {
			return this.firstAddressCountryValue ||
				this.firstAddressCityValue ||
				this.firstAddressStateValue
		},

		// work address
		firstAddressCountryValue() {
			const { dir_index_column1: address = {} } = this.allowFields
			return address.value
		},
		firstAddressCountryTitle() {
			const { dir_index_column1: address = {} } = this.allowFields
			return address.title
		},

		firstAddressCityValue() {
			const { member_city: address = {} } = this.allowFields
			return address.value
		},
		firstAddressCityTitle() {
			const { member_city: address = {} } = this.allowFields
			return address.title
		},

		firstAddressStateValue() {
			const { dir_index_column2: address = {} } = this.allowFields
			return address.value
		},
		firstAddressStateTitle() {
			const { dir_index_column2: address = {} } = this.allowFields
			return address.title
		}
	},
	methods: {
		...mapActions('app', ['showAlert', 'hideAlert']),
		...mapActions('event', ['getAttendee']),
		...mapActions('directory', ['getDirectory']),

		onTabSwitch() {
			this.successMessage = false
			this.errorMessage = false
		},
		formatUrl(url) {
			if (!url) {
				return '';
			}

			if (!url.startsWith('http://') && !url.startsWith('https://')) {
				if (url.startsWith('www.')) {
					url = 'https://' + url;
				} else {
					url = 'https://www.' + url;
				}
			}
			return url;
		},
		normalizeSettings(item) {
			const { setting, value } = item

			let settings = {}
			settings[setting.name] = value !== '0'

			return settings
		},
		dateCreated(originalDate) {
			return new Date(originalDate * 1000).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})
		},
		getTypeFieldsFromUrl(url) {
			const parts = url.split('/')
			return parts[parts.length - 1]
		},
		copyPhone(value) {
			if (!this.isShowAlert) {
				const phone = value.replace(/\D/g, '');

				this.$copyText(phone).then(() => {
					const closeTimeout = setTimeout(() => {
						this.hideAlert('')
					}, 3000)

					this.showAlert({ message: 'Phone number copied successfully.', closeTimeout })
				})
			}
		}
	},
	data() {
		return {
			searchIcon,
			arrowIcon,
			successMessage: false,
			errorMessage: false,
			attrs: {
				class: 'mb-6 mt-6',
				elevation: 0,
			},
			socialFieldNames: [
				'user_linkedin',
				'user_facebook',
				'user_twitter',
				'user_custom_url',
			],
			phoneFieldNames: [
				'user_work_phone',
				'user_cell_phone',
			],
		}
	},
	created() {
		if (this.$route.params.id) {
			this.getDirectory(+this.$route.params.id)
		}
	},
}
</script>

<style lang='scss'>
.v-list-item {
	padding: 0;

	.v-icon {
		margin-right: 12px;
	}
}

.directory-page {
	font-family: Montserrat, sans-serif;
}

.user {
	&__home-address,
	&__work-address {
		background: rgb(251, 251, 251);
		border-radius: 12px;
		border: 1px solid rgb(226, 226, 226);
	}

	&__brief-description {
		&__location {
			margin-left: 0 !important;
		}
	}

	&__phone-numbers > ul,
	&__networks > ul {
		list-style: none;
		padding: 0;
		margin: 0;
	}
	&__networks-link {
		word-break: break-word;
	}
}
</style>import { isEmpty } from 'lodash'

